 // components/ImageUploader.js
import React, { useState, useRef, useEffect } from 'react';
import {  Upload, Loader, Check, AlertCircle, XCircle, Link as LinkIcon, Search } from 'lucide-react';
import { useImageUpload } from '../../hooks/useImageUpload';
import { compressImageFile } from '../utils/imageCompression';
import { generateSafeFolderName } from '../utils/filenameSanitizer';
import WikimediaCommonsTab from './WikimediaCommonsTab';

const getBaseFilename = (metadata) => {
  if (metadata && metadata.filename) {
    return metadata.filename.replace(/\.json$/, '');
  }
  // Fallback to the old method if no filename exists
  return metadata && metadata.name ? metadata.name.toLowerCase().replace(/\s+/g, '_') : '';
};

const ImageUploader = ({
  imageUrl,
  onImageUploaded,
  itineraryMetadata,
  poiName,
  attributionData = null,
  label = "Image",
  previewSize = "h-40",
  accept = "image/*",
  maxWidth = 1200,
  maxSizeKB = 400,
  optimizationTargetKB = null,
  allowDownscale = true,
  disabled = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [inputUrl, setInputUrl] = useState(imageUrl || '');
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'url'
  const [selectedFile, setSelectedFile] = useState(null);
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState('');
  const [optimizedFile, setOptimizedFile] = useState(null);
  const [optimizedPreviewUrl, setOptimizedPreviewUrl] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationStats, setOptimizationStats] = useState(null);
  const [optimizationError, setOptimizationError] = useState('');
  const fileInputRef = useRef(null);
  const { uploadImage, isUploading, error: uploadError, progress } = useImageUpload();

  const stripHtml = (html) => {
    if (!html) return '';
    if (typeof DOMParser === 'undefined') {
      return String(html).replace(/<[^>]*>/g, '').trim();
    }
    const parsed = new DOMParser().parseFromString(String(html), 'text/html');
    return parsed.body?.textContent?.trim() || '';
  };

  const AttributionPanel = ({ data }) => {
    if (!data) return null;

    const rows = [
      ['License', data.licenseShortName || data.license],
      ['Author', data.artist],
      ['Credit', data.credit || data.attribution],
      ['Usage terms', data.usageTerms],
      ['Source', data.source],
    ].filter(([, value]) => Boolean(value));

    if (rows.length === 0 && !data.licenseUrl && !data.pageUrl) {
      return null;
    }

    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-2">
        <div className="font-medium text-slate-800">Wikimedia CC data</div>
        {rows.map(([label, value]) => (
          <div key={label}>
            <span className="font-medium">{label}:</span> {stripHtml(value)}
          </div>
        ))}
        {data.licenseUrl && (
          <div>
            <span className="font-medium">License URL:</span>{' '}
            <a href={data.licenseUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
              {data.licenseUrl}
            </a>
          </div>
        )}
        {data.pageUrl && (
          <div>
            <span className="font-medium">File page:</span>{' '}
            <a href={data.pageUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
              {data.pageUrl}
            </a>
          </div>
        )}
      </div>
    );
  };
  
  // Update local URL state when external URL changes
  useEffect(() => {
    setInputUrl(imageUrl || '');
  }, [imageUrl]);
  
  // Use upload error from hook
  useEffect(() => {
    if (uploadError) {
      setError(uploadError);
    }
  }, [uploadError]);

  useEffect(() => {
    return () => {
      if (originalPreviewUrl) URL.revokeObjectURL(originalPreviewUrl);
      if (optimizedPreviewUrl) URL.revokeObjectURL(optimizedPreviewUrl);
    };
  }, [originalPreviewUrl, optimizedPreviewUrl]);

  const clearOptimizationState = () => {
    if (originalPreviewUrl) URL.revokeObjectURL(originalPreviewUrl);
    if (optimizedPreviewUrl) URL.revokeObjectURL(optimizedPreviewUrl);
    setSelectedFile(null);
    setOriginalPreviewUrl('');
    setOptimizedFile(null);
    setOptimizedPreviewUrl('');
    setIsOptimizing(false);
    setOptimizationStats(null);
    setOptimizationError('');
  };

  const optimizeSelectedFile = async (file) => {
    setIsOptimizing(true);
    setOptimizationError('');

    try {
      const originalUrl = URL.createObjectURL(file);
      setOriginalPreviewUrl(originalUrl);
      setSelectedFile(file);

      const result = await compressImageFile(file, {
        maxWidth,
        targetSizeKB: Number.isFinite(optimizationTargetKB) && optimizationTargetKB > 0
          ? optimizationTargetKB
          : maxSizeKB,
        allowDownscale,
      });

      const optimizedUrl = URL.createObjectURL(result.blob);
      setOptimizedFile(result.file);
      setOptimizedPreviewUrl(optimizedUrl);
      setOptimizationStats({
        originalSizeKB: (result.originalSizeBytes / 1024).toFixed(1),
        optimizedSizeKB: (result.compressedSizeBytes / 1024).toFixed(1),
        width: result.width,
        height: result.height,
        mimeType: result.mimeType,
        quality: result.quality,
      });
    } catch (err) {
      console.error('Image optimization failed:', err);
      setOptimizationError(err.message || 'Failed to optimize image');
      setOptimizedFile(null);
      setOptimizedPreviewUrl('');
      setOptimizationStats(null);
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      setError(null);
      clearOptimizationState();
      await optimizeSelectedFile(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(`Upload failed: ${error.message}`);
    }
  };

  // Handle file selection
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await handleImageUpload(file);
    }
    e.target.value = '';
  };

  // Handle file drop
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleImageUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  // Handle URL input change
  const handleUrlChange = (e) => {
    setInputUrl(e.target.value);
  };

  // Handle URL input submit
  const handleUrlSubmit = () => {
    if (inputUrl && onImageUploaded) {
      onImageUploaded(inputUrl, null);
      setError(null);
    }
  };

  const uploadPreparedImage = async (fileToUpload) => {
    if (!fileToUpload) return;

    // Sanitize country name to replace spaces with underscores
    const sanitizedCountry = itineraryMetadata?.country
      ? itineraryMetadata.country.trim().toLowerCase().replace(/[^\w]/g, '_').replace(/_+/g, '_')
      : '';

    const canonicalFolderName = itineraryMetadata
      ? generateSafeFolderName(itineraryMetadata)
      : '';

    const metadata = {
      country: sanitizedCountry,
      itineraryName: canonicalFolderName || getBaseFilename(itineraryMetadata) || itineraryMetadata?.name,
      filename: canonicalFolderName ? `${canonicalFolderName}.json` : (itineraryMetadata?.filename || ''),
      poiName: poiName,
      objectNumber: itineraryMetadata?.objectNumber
    };

    const url = await uploadImage(fileToUpload, metadata, { maxSizeKB });
    if (url && onImageUploaded) {
      onImageUploaded(url);
      setInputUrl(url);
      clearOptimizationState();
    }
  };

  const handleUploadOptimized = async () => {
    try {
      if (!optimizedFile) {
        setError('Optimize an image first.');
        return;
      }
      setError(null);
      await uploadPreparedImage(optimizedFile);
    } catch (err) {
      console.error('Error uploading optimized image:', err);
      setError(`Upload failed: ${err.message}`);
    }
  };

  const handleUploadOriginal = async () => {
    try {
      if (!selectedFile) {
        setError('Select an image first.');
        return;
      }
      setError(null);
      await uploadPreparedImage(selectedFile);
    } catch (err) {
      console.error('Error uploading original image:', err);
      setError(`Upload failed: ${err.message}`);
    }
  };

  // Function to clear the current image
  const handleClearImage = () => {
    if (onImageUploaded) {
      onImageUploaded('', null);
      setInputUrl('');
    }
    clearOptimizationState();
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium mb-1">{label}</label>
      
      {/* Current Image Preview */}
      {imageUrl && (
        <div className="relative mb-4">
          <img
            src={imageUrl}
            alt="Preview"
            className={`w-full ${previewSize} object-cover rounded`}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/api/placeholder/400/300";
            }}
          />
          
          {/* Remove Image Button */}
          <button
            onClick={handleClearImage}
            disabled={disabled}
            className={`absolute top-2 right-2 p-1 rounded-full ${
              disabled
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600'
            }`}
            title={disabled ? 'Image editing disabled' : 'Remove image'}
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      <AttributionPanel data={attributionData} />
      
      {/* Tabs */}
      {!disabled && (
        <div className="flex border-b mb-4">
          <button
            className={`py-2 px-4 font-medium text-sm ${activeTab === 'upload' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('upload')}
          >
            <div className="flex items-center gap-1">
              <Upload className="w-4 h-4" />
              <span>Upload</span>
            </div>
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm ${activeTab === 'url' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('url')}
          >
            <div className="flex items-center gap-1">
              <LinkIcon className="w-4 h-4" />
              <span>URL</span>
            </div>
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm ${activeTab === 'wikimedia' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('wikimedia')}
          >
            <div className="flex items-center gap-1">
              <Search className="w-4 h-4" />
              <span>Wikimedia Commons</span>
            </div>
          </button>
        </div>
      )}
      
      {/* URL Input Tab */}
      {activeTab === 'url' && !disabled && (
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 p-2 border rounded"
            placeholder="Enter image URL"
            value={inputUrl}
            onChange={handleUrlChange}
          />
          <button
            onClick={handleUrlSubmit}
            className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
          >
            <LinkIcon className="w-4 h-4" />
            Use URL
          </button>
        </div>
      )}

      {/* Wikimedia Commons Tab */}
      {activeTab === 'wikimedia' && !disabled && (
        <WikimediaCommonsTab
          onImageUploaded={onImageUploaded}
          itineraryMetadata={itineraryMetadata}
          poiName={poiName}
          maxSizeKB={maxSizeKB}
        />
      )}

      {/* Upload Tab */}
      {activeTab === 'upload' && !disabled && (
        <div>
          {/* Drag & Drop Area */}
          <div 
            className={`relative border-2 border-dashed rounded-lg p-4 ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept={accept}
              className="hidden"
            />
            
            <div className="flex flex-col items-center justify-center py-6">
              <Upload className="w-12 h-12 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500 text-center mb-2">
                {isUploading ? 'Uploading...' : 'Drag & drop an image here, or click to select'}
              </p>
              <p className="text-xs text-gray-400 text-center mb-2">
                {Number.isFinite(maxSizeKB) && maxSizeKB > 0
                  ? `Maximum file size: ${maxSizeKB}KB`
                  : 'No upload size limit'}
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || disabled}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 flex items-center gap-1"
              >
                {isUploading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Uploading ({progress}%)
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Select Image
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-slate-800">Image Optimization</p>
                <p className="text-xs text-slate-500">
                  {allowDownscale
                    ? `Max width ${maxWidth}px, ${Number.isFinite(optimizationTargetKB) && optimizationTargetKB > 0 ? `target size ${optimizationTargetKB}KB` : 'no target size limit'}. WebP is preferred when supported.`
                    : `Original dimensions preserved, ${Number.isFinite(optimizationTargetKB) && optimizationTargetKB > 0 ? `target size ${optimizationTargetKB}KB` : 'no target size limit'}. WebP is preferred when supported.`}
                </p>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isOptimizing || disabled}
                className="px-3 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:bg-gray-400 text-sm"
              >
                {selectedFile ? 'Choose Different Image' : 'Select Image to Optimize'}
              </button>
            </div>

            {isOptimizing && (
              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <Loader className="w-4 h-4 animate-spin" />
                Optimizing image...
              </div>
            )}

            {optimizationError && (
              <div className="text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {optimizationError}
              </div>
            )}

            {selectedFile && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded border bg-white p-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">Original</p>
                  <img
                    src={originalPreviewUrl}
                    alt="Original preview"
                    className="w-full h-40 object-cover rounded border"
                  />
                  <div className="mt-2 text-xs text-slate-500">
                    {selectedFile.name} · {(selectedFile.size / 1024).toFixed(1)} KB
                  </div>
                </div>

                <div className="rounded border bg-white p-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">Optimized</p>
                  {optimizedPreviewUrl ? (
                    <>
                      <img
                        src={optimizedPreviewUrl}
                        alt="Optimized preview"
                        className="w-full h-40 object-cover rounded border"
                      />
                      <div className="mt-2 text-xs text-slate-500 space-y-1">
                        <div>{optimizedFile?.name || 'Optimized image'}</div>
                        {optimizationStats && (
                          <>
                            <div>{optimizationStats.width} x {optimizationStats.height}</div>
                            <div>{optimizationStats.optimizedSizeKB} KB · {optimizationStats.mimeType.replace('image/', '').toUpperCase()} · q{Math.round((optimizationStats.quality || 0) * 100)}</div>
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="h-40 rounded border border-dashed flex items-center justify-center text-sm text-slate-500">
                      No optimized preview yet
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleUploadOptimized}
                disabled={!optimizedFile || isUploading || isOptimizing || disabled}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 text-sm"
              >
                {isUploading ? 'Uploading...' : 'Upload Optimized Image'}
              </button>
              <button
                onClick={handleUploadOriginal}
                disabled={!selectedFile || isUploading || isOptimizing || disabled}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:bg-gray-100 text-sm"
              >
                Upload Original
              </button>
              <button
                onClick={clearOptimizationState}
                disabled={!selectedFile || isUploading || isOptimizing || disabled}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:bg-gray-100 text-sm"
              >
                Clear Selection
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          {isUploading && (
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          )}
        </div>
      )}

      {/* Disabled State Message */}
      {disabled && (
        <div className="text-gray-500 text-sm flex items-center gap-1 p-4 bg-gray-50 rounded border">
          <AlertCircle className="w-4 h-4" />
          Image editing is disabled due to permission settings
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="text-red-500 text-sm flex items-center gap-1 mt-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      
      {/* Success Message */}
      {imageUrl && !error && !isUploading && (
        <div className="text-green-500 text-sm flex items-center gap-1 mt-2">
          <Check className="w-4 h-4" />
          Image set successfully
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
